import { useSyncExternalStore } from 'react';

import { VerificationErrorEnum } from 'contexts/MatrixDeviceVerificationContext/utils';
import { SessionStateKeys, dispatchSessionStorageUpdateEvent, removeSessionStorageValue } from 'hooks/useSessionState';

/**
 * Login-time secret storage unlock bus.
 *
 * `useStartMatrixClient` runs as imperative async code outside React, so the coordination between it
 * and the `LoginSecretStorageUnlockGate` UI lives in this module-level singleton (mirroring the
 * `secretStorageCallbacks` singleton in the same domain) rather than in `MatrixContext`'s value
 * object — keeping it off the provider avoids re-rendering the whole tree on every unlock state
 * change. The gate subscribes to the snapshot via `useLoginSecretStorageUnlock`.
 *
 * Flow:
 * - `waitForLoginSecretStorageUnlock(unlock)` — called by the start client; opens the gate and
 *   resolves once a submitted code unlocks successfully, or rejects with
 *   `LoginSecretStorageUnlockCancelledError` if the user cancels.
 * - `submitLoginSecretStorageUnlock(code)` — called by the gate; runs the provided unlock executor.
 *   On `INVALID_SECURITY_CODE` it surfaces an inline error and keeps the gate open for retry.
 * - `cancelLoginSecretStorageUnlock()` — called by the gate; aborts the pending unlock.
 */

export class LoginSecretStorageUnlockCancelledError extends Error {
  constructor() {
    super('Login secret storage unlock cancelled by user');
    this.name = 'LoginSecretStorageUnlockCancelledError';
  }
}

/**
 * Which stage of the login flow the unlock gate is serving:
 * - `pre_sync`: before `startClient` runs — the sync loop has not started yet.
 *   `requestOwnUserVerification` will fail here; "verify on another device" must
 *   resolve this gate first so the sync can start.
 * - `post_bootstrap`: after `startClient` + first sync — the sync loop is running
 *   and cross-signing public keys are available. `requestOwnUserVerification` works.
 */
export type LoginSecretStorageUnlockPhase = 'pre_sync' | 'post_bootstrap';

export interface LoginSecretStorageUnlockState {
  isRequired: boolean;
  isSubmitting: boolean;
  errorKey: VerificationErrorEnum | null;
  /** Which gate phase is currently open, or null when idle. */
  phase: LoginSecretStorageUnlockPhase | null;
}

type UnlockExecutor = (securityCode: string) => Promise<void>;

/** Returns the phase of the currently-open login unlock gate, or null when idle. */
export const getLoginSecretStorageUnlockPhase = (): LoginSecretStorageUnlockPhase | null => state.phase;

/**
 * Marks that the user chose "verify on another device" during login. Set when the pre_sync gate is
 * bypassed so the SAS stack stays login-aware while `startClient` runs and the post_bootstrap gate
 * re-opens. Does not affect the gate snapshot — it is a plain module-level signal.
 */
export const markLoginSasVerificationPending = (): void => {
  loginSasVerificationPending = true;
};

/** Clears the durable login-SAS signal (completion, cancel, or no verified peer device). */
export const clearLoginSasVerificationPending = (): void => {
  loginSasVerificationPending = false;
};

/** True while a login-time "verify on another device" flow is in progress (survives gate close). */
export const isLoginSasVerificationPending = (): boolean => loginSasVerificationPending;

/**
 * Marks that the user successfully reset their keys during login unlock. Set by `ResetKeysModal`
 * before it resolves the gate so `useStartMatrixClient` skips the pre-sync probe and post-bootstrap
 * safety net when it resumes — on poisoned accounts the broken server identity keeps satisfying
 * `needsSecretStorageUnlock`, so re-gating after a reset would loop forever (BUG-013). The fresh
 * keys are already local, so re-gating serves no purpose. Cleared on cancel / logout.
 */
export const markLoginUnlockResetCompleted = (): void => {
  loginUnlockResetCompleted = true;
};

/** True once a reset has completed during the current login unlock session. */
export const isLoginUnlockResetCompleted = (): boolean => loginUnlockResetCompleted;

/**
 * Clears the durable login-unlock-reset signal. Called by `BootstrapSecurityCodeGate` once the
 * create-code modal has opened — from that point on the signal has served its purpose (preventing
 * re-gating and suppressing the verification picker) and must be cleared so the picker can resume
 * normal operation after the create-code flow completes (BUG-021).
 */
export const clearLoginUnlockResetCompleted = (): void => {
  loginUnlockResetCompleted = false;
};

/**
 * Marks that a broken cross-signing identity was detected during login unlock. Set when an unlock
 * error maps to `BROKEN_CROSS_SIGNING_IDENTITY` so the method picker can hide "Verify with security
 * code" — on a poisoned account the security code can never succeed (`importCrossSigningKeys`
 * always fails), so offering it just wastes a round-trip (BUG-013 Fix B). Cleared on cancel / logout.
 */
export const markLoginUnlockBrokenIdentity = (): void => {
  loginUnlockBrokenIdentity = true;
};

/** True once a broken cross-signing identity has been detected during the current login session. */
export const isLoginUnlockBrokenIdentity = (): boolean => loginUnlockBrokenIdentity;

/**
 * Marks that a cross-signing reset must run once `startClient` has started, instead of during the
 * `pre_sync` gate. `crypto.resetEncryption()` hangs when the client is not yet started —
 * `setAccountData before the client is started` warnings appear, then the SDK stalls before
 * `device_signing/upload` (Faro HchDv0aF8q for poisoned accounts, Faro b1XUCAraRw for healthy
 * accounts). `ResetKeysModal` sets this after the user authorises in MAS, then resolves the gate so
 * `startClient` runs; `useStartMatrixClient` then handles the reset with a started client
 * (BUG-013 Fix C, BUG-015). Cleared once consumed, and on cancel / logout.
 */
export const markLoginUnlockDeferredResetPending = (): void => {
  loginUnlockDeferredResetPending = true;
};

/** True while a cross-signing reset is deferred to run after `startClient`. */
export const isLoginUnlockDeferredResetPending = (): boolean => loginUnlockDeferredResetPending;

/** Clears the deferred-reset signal once `useStartMatrixClient` has consumed it. */
export const clearLoginUnlockDeferredResetPending = (): void => {
  loginUnlockDeferredResetPending = false;
};

/**
 * Marks that the deferred reset is for a **healthy** account (not a poisoned one). Healthy accounts
 * require a fresh `device_signing/upload` UIA session after `startClient` — `masAlreadyAuthorised`
 * won't work because the standard-UIA server ties the authorisation to a specific session ID that
 * was not yet allocated when the user went through MAS at click-time. `useStartMatrixClient` reads
 * this to decide whether to auto-run the reset (poisoned path) or delegate to `PostLoginResetGate`
 * (healthy path, BUG-015 Fix B). Cleared once consumed, and on cancel / logout.
 */
export const markLoginUnlockDeferredResetNeedsFullFlow = (): void => {
  loginUnlockDeferredResetNeedsFullFlow = true;
};

/** True when the deferred reset must run the full UIA flow post-login (BUG-015). */
export const isLoginUnlockDeferredResetNeedsFullFlow = (): boolean => loginUnlockDeferredResetNeedsFullFlow;

/** Clears the full-flow flag once `useStartMatrixClient` has consumed it. */
export const clearLoginUnlockDeferredResetNeedsFullFlow = (): void => {
  loginUnlockDeferredResetNeedsFullFlow = false;
};

interface PendingUnlock {
  unlock: UnlockExecutor;
  resolve: () => void;
  reject: (error: unknown) => void;
}

const IDLE_STATE: LoginSecretStorageUnlockState = {
  isRequired: false,
  isSubmitting: false,
  errorKey: null,
  phase: null,
};

let state: LoginSecretStorageUnlockState = IDLE_STATE;
let pending: PendingUnlock | null = null;
let loginUnlockCancelHandler: (() => void) | null = null;
// Durable signal that the user chose "verify on another device" during login. Unlike `pending`
// (which is cleared the moment the gate resolves), this survives the pre_sync → post_bootstrap
// transition: the pre_sync bypass closes the gate so `startClient` can run, and the SAS stack must
// keep treating the flow as a login unlock across that gap (otherwise the waiting modal never opens
// and the user is stranded on a spinner — BUG-006). Cleared on completion, cancel, or when no
// verified peer device turns out to exist.
let loginSasVerificationPending = false;
// Durable signal that the user reset their keys during this login session (BUG-013 Fix A). Survives
// the gate closing so `useStartMatrixClient` skips re-gating on resume. Cleared on cancel / reset.
let loginUnlockResetCompleted = false;
// Durable signal that a broken cross-signing identity was detected this login session (BUG-013
// Fix B). Hides the security code option in the picker. Cleared on cancel / reset.
let loginUnlockBrokenIdentity = false;
// Durable signal that a cross-signing reset must run after `startClient` rather than during the
// pre_sync gate (BUG-013 Fix C, BUG-015). Set by ResetKeysModal, consumed by useStartMatrixClient.
let loginUnlockDeferredResetPending = false;
// Durable signal that the deferred reset needs a full UIA flow after startClient (BUG-015). Set
// for healthy accounts (non-poisoned) where masAlreadyAuthorised won't work.
let loginUnlockDeferredResetNeedsFullFlow = false;
const listeners = new Set<() => void>();

const setState = (next: LoginSecretStorageUnlockState): void => {
  state = next;
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = (): LoginSecretStorageUnlockState => state;

/** Opens the login unlock gate and waits for a successful unlock or a cancel. */
export const waitForLoginSecretStorageUnlock = (
  unlock: UnlockExecutor,
  phase: LoginSecretStorageUnlockPhase
): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    // BUG-020: a stale create-code flag from a *prior* incomplete reset must not make the picker
    // think resetAlreadyHandled and self-dismiss during login unlock.
    // BUG-021: only clear the flag when no valid post-reset intent exists in this session
    // (isLoginUnlockResetCompleted). If a reset already ran this session the flag was set on purpose
    // and wiping it here would silently drop the scheduled create-code prompt.
    if (!isLoginUnlockResetCompleted()) {
      removeSessionStorageValue(SessionStateKeys.NEEDS_SECURITY_CODE_PROMPT_AFTER_BOOTSTRAP);
      dispatchSessionStorageUpdateEvent(SessionStateKeys.NEEDS_SECURITY_CODE_PROMPT_AFTER_BOOTSTRAP, null);
    }

    pending = { unlock, resolve, reject };
    setState({ isRequired: true, isSubmitting: false, errorKey: null, phase });
  });

/**
 * Runs the pending unlock with the submitted security code. Resolves the gate on success. On an
 * invalid code it sets `errorKey` and keeps the gate open so the user can retry; the rejection is
 * re-thrown so the caller can reset its input.
 */
export const submitLoginSecretStorageUnlock = async (securityCode: string): Promise<void> => {
  const current = pending;
  if (!current) return;

  setState({ isRequired: true, isSubmitting: true, errorKey: null, phase: state.phase });

  try {
    await current.unlock(securityCode);
    pending = null;
    setState(IDLE_STATE);
    current.resolve();
  } catch (error) {
    setState({
      isRequired: true,
      isSubmitting: false,
      errorKey: mapUnlockErrorToKey(error),
      phase: state.phase,
    });
    throw error;
  }
};

/**
 * Maps the executor error message to the inline `errorKey` shown by the gate UI. Specific known
 * errors (invalid PIN, broken cross-signing identity from a poisoned account) get dedicated keys
 * so the modal can render actionable guidance; everything else falls back to UNKNOWN_ERROR.
 */
const mapUnlockErrorToKey = (error: unknown): VerificationErrorEnum => {
  if (!(error instanceof Error)) return VerificationErrorEnum.UNKNOWN_ERROR;
  if (error.message === VerificationErrorEnum.INVALID_SECURITY_CODE) {
    return VerificationErrorEnum.INVALID_SECURITY_CODE;
  }
  if (error.message === VerificationErrorEnum.BROKEN_CROSS_SIGNING_IDENTITY) {
    // Remember the broken identity for the rest of the session so the picker hides the security
    // code option, which can never succeed on a poisoned account (BUG-013 Fix B).
    markLoginUnlockBrokenIdentity();
    return VerificationErrorEnum.BROKEN_CROSS_SIGNING_IDENTITY;
  }
  return VerificationErrorEnum.UNKNOWN_ERROR;
};

/** Completes the pending login unlock without running the PIN executor (e.g. after SAS verification). */
export const completeLoginSecretStorageUnlock = (): void => {
  const current = pending;
  if (!current) return;

  pending = null;
  setState(IDLE_STATE);
  current.resolve();
};

/** True while `startMatrixClient` is waiting on the login unlock gate. */
export const isLoginSecretStorageUnlockActive = (): boolean => pending !== null;

/** Registered by `LoginSecretStorageUnlockGate` so nested modals can cancel login unlock. */
export const setLoginSecretStorageUnlockCancelHandler = (handler: (() => void) | null): void => {
  loginUnlockCancelHandler = handler;
};

export const invokeLoginSecretStorageUnlockCancel = (): void => {
  loginUnlockCancelHandler?.();
};

/** Aborts the pending unlock, rejecting the start client's wait with a cancel error. */
export const cancelLoginSecretStorageUnlock = (): void => {
  const current = pending;
  if (!current) return;

  pending = null;
  loginSasVerificationPending = false;
  loginUnlockResetCompleted = false;
  loginUnlockBrokenIdentity = false;
  loginUnlockDeferredResetPending = false;
  loginUnlockDeferredResetNeedsFullFlow = false;
  setState(IDLE_STATE);
  current.reject(new LoginSecretStorageUnlockCancelledError());
};

/** React hook returning the current login unlock snapshot for the gate UI. */
export const useLoginSecretStorageUnlock = (): LoginSecretStorageUnlockState =>
  useSyncExternalStore(subscribe, getSnapshot);

/** Test-only: reset the bus to its idle state between cases. */
export const resetLoginSecretStorageUnlockForTests = (): void => {
  pending = null;
  loginUnlockCancelHandler = null;
  loginSasVerificationPending = false;
  loginUnlockResetCompleted = false;
  loginUnlockBrokenIdentity = false;
  loginUnlockDeferredResetPending = false;
  loginUnlockDeferredResetNeedsFullFlow = false;
  state = IDLE_STATE;
  listeners.clear();
};
